import { z } from "zod";

const PERSONAL_COMPOSER_MAX_CHARS = 100_000;
export const PERSONAL_COMPOSER_RETENTION_MS = 7 * 24 * 3_600_000;

const ComposerRevisionSchema = z.number().int().nonnegative().max(2_147_483_647);
export const ComposerThreadVersionSchema = z.string().regex(/^tv1:[a-f0-9]{64}$/u);
const Words = z.string().max(PERSONAL_COMPOSER_MAX_CHARS);
const At = z.iso.datetime({ offset: true });

const ComposerSignatureSchema = z.strictObject({
  source: z.enum(["leadcue", "bison"]),
  senderAddress: z.string().max(320),
  html: Words,
  plainText: Words,
  digest: z.string().regex(/^[a-f0-9]{64}$/u),
});

// `body` is text before its explicitly stored signature. `opaque` is the whole editor contents;
// neither server nor client attempts to find a signature boundary inside opaque email text.
export const PersonalComposerContentSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("body"),
    format: z.literal("text"),
    bodyText: Words,
    signature: ComposerSignatureSchema.nullable(),
    plainText: Words,
    html: Words,
  }),
  z.strictObject({
    kind: z.literal("opaque"),
    format: z.enum(["text", "html"]),
    plainText: Words,
    html: Words,
    // Display lineage only. This is never an approval or permission to use the agent send route.
    fromDraft: z.number().int().nonnegative().nullable(),
  }),
]);
export type PersonalComposerContent = z.infer<typeof PersonalComposerContentSchema>;

export const PersonalComposerSchema = z
  .strictObject({
    revision: ComposerRevisionSchema,
    content: PersonalComposerContentSchema.nullable(),
    emptyReason: z.enum(["never_saved", "cleared", "expired", "sent"]).nullable(),
    sourceMessageId: z.string().min(1).max(200).nullable(),
    sourceThreadVersion: ComposerThreadVersionSchema.nullable(),
    updatedAt: At.nullable(),
    expiresAt: At.nullable(),
    lastWriter: z
      .strictObject({
        kind: z.enum(["browser", "mcp"]),
        clientId: z.string().min(1).max(2048),
      })
      .nullable(),
  })
  .refine((value) =>
    value.content === null
      ? value.emptyReason !== null && value.expiresAt === null
      : value.emptyReason === null &&
        value.revision > 0 &&
        value.updatedAt !== null &&
        value.expiresAt !== null &&
        value.sourceThreadVersion !== null,
  );
export type PersonalComposer = z.infer<typeof PersonalComposerSchema>;

export const PersonalComposerReadSchema = z.strictObject({
  composer: PersonalComposerSchema,
  threadVersion: ComposerThreadVersionSchema,
  sourceMessageId: z.string().min(1).max(200).nullable(),
  observedAt: At,
  appLink: z.url(),
});
export type PersonalComposerRead = z.infer<typeof PersonalComposerReadSchema>;

const Mutation = z.strictObject({
  expectedRevision: ComposerRevisionSchema,
  threadVersion: ComposerThreadVersionSchema,
  sourceMessageId: z.string().min(1).max(200).nullable(),
  idempotencyKey: z.string().min(1).max(200),
});
export const SaveComposerBodySchema = Mutation.extend({
  bodyText: Words,
  // Required for replacing an opaque draft. The client must obtain explicit user confirmation;
  // this assertion is client supplied, not a server attestation that a human clicked a button.
  replaceOpaque: z
    .strictObject({
      revision: ComposerRevisionSchema,
      confirmedByUser: z.literal(true),
    })
    .optional(),
});
export type SaveComposerBody = z.infer<typeof SaveComposerBodySchema>;

export const SaveComposerFullSchema = Mutation.extend({
  format: z.enum(["text", "html"]),
  plainText: Words,
  html: Words,
  fromDraft: z.number().int().nonnegative().nullable(),
});
export type SaveComposerFull = z.infer<typeof SaveComposerFullSchema>;
export const ClearPersonalComposerSchema = Mutation;
export type ClearPersonalComposer = z.infer<typeof ClearPersonalComposerSchema>;

export const PersonalComposerWriteSchema = z.strictObject({
  replayed: z.boolean(),
  savedRevision: ComposerRevisionSchema,
  savedAt: At,
  retryUntil: At,
  isCurrent: z.boolean(),
  composer: PersonalComposerSchema,
  appLink: z.url(),
  sent: z.literal(false),
});
export type PersonalComposerWrite = z.infer<typeof PersonalComposerWriteSchema>;

const PERSONAL_COMPOSER_REFUSALS = [
  "not_found",
  "forbidden",
  "composer_changed",
  "stale_thread",
  "source_message_not_found",
  "opaque_replacement_required",
  "idempotency_key_reused",
  "rendered_draft_too_large",
] as const;
export type PersonalComposerRefusal = (typeof PERSONAL_COMPOSER_REFUSALS)[number];
